import requestor from "requestor";
import React, {useState, useEffect, useCallback} from "react";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import {push} from "react-router-redux";
import {RouterState, InjectedRouter} from "react-router/lib/Router";

import CircularProgress from "@material-ui/core/CircularProgress";

import Permissioner from "utils/permissioner";
import {isInitialised} from "utils/uninitialised";
import setTitle from "utils/set_title";
import LlmRunListComponent from "../components/llm_run_list";

import {parseQuery, buildQuery} from "utils/uri";
import {getLlmRunQueryParams} from "../../utils/filtering/get_llm_run_query_params";
import pickLlmRunFiltersFromURL from "../../utils/filtering/pick_llm_run_filters_from_url";
import formatDateToLocalISOString from "../../utils/format_date_local_iso_string";

import llmRunsFetch, {
  GetLlmRunsAction,
} from "modules/flowmaster/actions/llm_runs_fetch";
import llmRunsClear from "modules/flowmaster/actions/llm_runs_clear";
import rerunTestsAction from "modules/flowmaster/actions/llm_run_rerun_tests";
import rerunMultipleTestsAction from "modules/flowmaster/actions/llm_run_rerun_multiple_tests";

import {
  LlmRunInfo,
  LlmRunId,
  LlmRunFilters,
} from "common/flowmaster/types/llm_run";
import {User} from "common/types/user";
import {OrganisationId} from "common/types/organisation";
import getNewSearchParams from "routes/llm_run/utils/filtering/update_url_params";
import _ from "underscore";

type Props = {
  llmRuns: LlmRunInfo[];
  user: User;
  router: RouterState & InjectedRouter;
  location: Location;
  params: {
    organisationId: string;
  };
  llmRunsFetch: GetLlmRunsAction["action"];
  rerunTests: (organisation_id: OrganisationId, llmRunId: LlmRunId) => void;
  rerunMultipleTests: (
    organisation_id: OrganisationId,
    llmRunIds: LlmRunId[],
  ) => void;
  dispatch: (args: unknown) => unknown;
};

function LlmRunListContainer(props: Props) {
  const llmRunValues = getLlmRunQueryParams(
    pickLlmRunFiltersFromURL(props.router),
  );

  const maxRuns = llmRunValues.max_runs ?? sessionStorage.maxLlmRuns ?? 100;
  const [isLoading, setLoading] = useState(false);
  const [isDeveloperMode, setIsDeveloperMode] = useState(
    props.user.is_developer,
  );

  const fetchData = useCallback(
    async queryParams => {
      setLoading(true);
      await props.llmRunsFetch(
        parseInt(props.params.organisationId, 10),
        queryParams,
        maxRuns,
      );
      setLoading(false);
    },
    [props.llmRunsFetch, props.params.organisationId, maxRuns],
  );

  const handleRefresh = () => {
    const queryParams = pickLlmRunFiltersFromURL(props.router);
    fetchData(queryParams);
  };

  useEffect(() => {
    const queryParams = pickLlmRunFiltersFromURL(props.router);

    if (!queryParams.from || !queryParams.max_runs) {
      const additionalParams = {} as Partial<LlmRunFilters>;
      if (!queryParams.from) {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        oneWeekAgo.setHours(0, 0, 0, 0);

        additionalParams.from = formatDateToLocalISOString(oneWeekAgo);
      }

      if (!queryParams.max_runs) {
        additionalParams.max_runs = maxRuns;
      }

      const {pathname, search} = props.location;
      const currentParams = parseQuery(search);
      const newSearch = buildQuery({
        ...currentParams,
        ...additionalParams,
      });
      props.dispatch(push(pathname + newSearch));
    } else {
      fetchData(queryParams);
    }

    return () => {
      setLoading(false);
    };
  }, [
    JSON.stringify(pickLlmRunFiltersFromURL(props.router)),
    JSON.stringify(_.omit(props.location, ["key"])),
  ]);

  useEffect(() => {
    setIsDeveloperMode(props.user.is_developer);
  }, [props.user]);

  const updateMaxRuns = value => {
    sessionStorage.maxLlmRuns = value;
    updateUrlParams({max_runs: value});
  };

  const updateUrlParams = obj => {
    const {pathname, search} = props.location;
    const newSearch = getNewSearchParams(obj, search);
    if (search !== newSearch) {
      props.dispatch(push(pathname + newSearch));
    }
  };

  const shouldRenderContainer = isInitialised([props.llmRuns, props.user]);
  if (!shouldRenderContainer || !props.user.permissions) {
    return (
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  const permissioner = new Permissioner(props.user);
  if (
    !(permissioner.hasPermission("get-workflows") || permissioner.isAdmin())
  ) {
    return permissioner.getNoPermissionMessage();
  }
  const handleToggleDeveloperMode = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsDeveloperMode(event.target.checked);
  };

  setTitle("LLM Run List");
  const organisationId = parseInt(props.router.params.organisationId, 10);

  const rerunTestsHandler = (llmRunId: LlmRunId) => {
    props.rerunTests(organisationId, llmRunId);
  };
  const rerunMultipleTestsHandler = (llmRunIds: LlmRunId[]) => {
    props.rerunMultipleTests(organisationId, llmRunIds);
  };

  return (
    <LlmRunListComponent
      isLoading={isLoading}
      organisationId={organisationId}
      llmRuns={props.llmRuns}
      values={llmRunValues}
      updateValue={obj => {
        updateUrlParams(obj);
      }}
      goToRun={id => {
        props.router.push(
          `/organisation/${organisationId}/llm_run/${id}/detail`,
        );
      }}
      isDeveloperMode={isDeveloperMode}
      onDeveloperModeToggle={handleToggleDeveloperMode}
      maxRuns={maxRuns}
      setMaxRuns={updateMaxRuns}
      rerunTests={rerunTestsHandler}
      rerunMultipleTests={rerunMultipleTestsHandler}
      onRefresh={handleRefresh}
    />
  );
}

function select(state, props) {
  return {
    params: props.params,
    organisation: state.organisation,
    user: state.user,
    llmRuns: state.llmRuns,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        llmRunsFetch: llmRunsFetch(requestor),
        rerunTests: rerunTestsAction(requestor),
        rerunMultipleTests: rerunMultipleTestsAction(requestor),
        llmRunsClear,
      },
      dispatch,
    ),
  };
}

export default connect(select, mapDispatchToProps)(LlmRunListContainer);
